import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, LogOut, Search, RefreshCw, Trash2, Phone, Mail, MapPin, Loader2, Inbox, Images } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, fetchQuotes, updateQuoteStatus, deleteQuote } from "@/lib/api";
import { BUSINESS } from "@/data/site";
import { EASE } from "@/components/motion/Reveal";
import { AdminGallery } from "@/components/AdminGallery";
import { WaterSplash } from "@/components/motion/WaterSplash";
import { useFirstClickSplash } from "@/lib/sound";

const STATUSES = ["new", "contacted", "scheduled", "completed", "closed"];
const STATUS_STYLE = {
  new: "bg-brand text-white",
  contacted: "bg-cyanglow/20 text-cyanglow border border-cyanglow/40",
  scheduled: "bg-gold/20 text-gold border border-gold/40",
  completed: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40",
  closed: "bg-white/10 text-slate-400 border border-white/10",
};

const Login = ({ onSuccess }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const logoRef = useRef(null);
  const [splash, setSplash] = useState(0);
  useFirstClickSplash(useCallback(() => setSplash((n) => n + 1), []));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(code);
      onSuccess(code);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(err?.response?.status === 429 && typeof detail === "string" ? detail : "Wrong passcode, mate.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen grid place-items-center px-5 blueprint">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="w-full max-w-sm bg-panel/90 border border-white/10 rounded-2xl p-8 shadow-2xl"
        data-testid="admin-login-form"
      >
        <div className="relative w-fit mx-auto mb-2">
          <WaterSplash originRef={logoRef} delay={0.4} trigger={splash} />
          <motion.img
            ref={logoRef}
            src="/logo.png"
            alt="Big Rickie's"
            className="relative h-52 w-auto drop-shadow-[0_0_30px_rgba(107,188,253,0.4)]"
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: [0.7, 1.1, 0.97, 1], opacity: [0, 1, 1, 1] }}
            transition={{ duration: 1, delay: 0.2, times: [0, 0.5, 0.75, 1], ease: EASE }}
            data-testid="admin-login-logo-img"
          />
        </div>
        <div className="grid place-items-center w-10 h-10 rounded-full bg-brand text-white mx-auto mb-5">
          <Lock size={16} strokeWidth={2.5} />
        </div>
        <h1 className="h-display text-3xl text-white text-center">Rickie's Office</h1>
        <p className="text-center text-sm text-slate-500 mt-2 mb-8">Enter the passcode to view quote requests.</p>
        <input
          type="password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Passcode"
          className="field text-center tracking-[0.3em]"
          autoFocus
          data-testid="admin-passcode-input"
        />
        <button type="submit" disabled={loading || !code} className="btn-primary w-full mt-4 !text-base disabled:opacity-60" data-testid="admin-login-btn">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Unlock"}
        </button>
        <a href="/" className="block text-center text-xs text-slate-500 hover:text-slate-300 mt-6 font-mono uppercase tracking-widest" data-testid="admin-back-home-link">
          ← Back to site
        </a>
      </motion.form>
    </div>
  );
};

const fmtDate = (iso) => new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const QuoteRow = ({ q, onStatus, onDelete }) => (
  <motion.li
    layout
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.98 }}
    transition={{ duration: 0.35, ease: EASE }}
    className="bg-panel/80 border border-white/10 rounded-xl p-5 sm:p-6 grid lg:grid-cols-12 gap-4 lg:gap-6 items-start hover:border-white/20 transition-colors"
    data-testid={`quote-row-${q.id}`}
  >
    <div className="lg:col-span-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={`px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLE[q.status]}`} data-testid={`quote-status-badge-${q.id}`}>{q.status}</span>
        <span className="font-mono text-[10px] text-slate-500">{fmtDate(q.created_at)}</span>
        {q.notified === true && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid={`quote-emailed-badge-${q.id}`}><Mail size={10} /> Emailed</span>}
        {q.notified === false && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-widest bg-red-500/15 text-red-300 border border-red-500/30" data-testid={`quote-email-failed-badge-${q.id}`}><Mail size={10} /> Email failed</span>}
      </div>
      <div className="font-display font-extrabold uppercase text-xl text-white">{q.name}</div>
      <div className="text-xs text-slate-500">{q.property_type}</div>
    </div>
    <div className="lg:col-span-3 space-y-1.5 text-sm">
      <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-white hover:text-brand-light transition-colors" data-testid={`quote-phone-${q.id}`}><Phone size={13} className="text-brand-light" />{q.phone}</a>
      {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-slate-300 hover:text-brand-light transition-colors break-all"><Mail size={13} className="text-brand-light" />{q.email}</a>}
      <div className="flex items-center gap-2 text-slate-300"><MapPin size={13} className="text-brand-light" />{q.suburb}</div>
    </div>
    <div className="lg:col-span-4">
      <div className="font-display font-bold uppercase tracking-wider text-sm text-brand-light mb-1">{q.service}</div>
      <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-wrap">{q.message || <span className="italic text-slate-600">No message</span>}</p>
    </div>
    <div className="lg:col-span-2 flex lg:flex-col gap-2 lg:items-stretch">
      <select value={q.status} onChange={(e) => onStatus(q.id, e.target.value)} className="field !py-2 !text-xs uppercase font-mono tracking-wider flex-1" data-testid={`quote-status-select-${q.id}`}>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button onClick={() => onDelete(q.id)} className="grid place-items-center px-3 py-2 rounded-md border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-400/50 transition-colors" aria-label="Delete" data-testid={`quote-delete-btn-${q.id}`}>
        <Trash2 size={15} />
      </button>
    </div>
  </motion.li>
);

const Dashboard = ({ passcode, onLogout }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("quotes");
  const headerLogoRef = useRef(null);
  const [splash, setSplash] = useState(0);
  useFirstClickSplash(useCallback(() => setSplash((n) => n + 1), []));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setQuotes(await fetchQuotes(passcode));
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Couldn't load quotes.");
    } finally {
      setLoading(false);
    }
  }, [passcode, onLogout]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    const updated = await updateQuoteStatus(passcode, id, status);
    setQuotes((qs) => qs.map((x) => (x.id === id ? updated : x)));
    toast.success(`Marked as ${status}`);
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    await deleteQuote(passcode, id);
    setQuotes((qs) => qs.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const counts = useMemo(() => STATUSES.reduce((a, s) => ({ ...a, [s]: quotes.filter((x) => x.status === s).length }), { all: quotes.length }), [quotes]);
  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    return quotes.filter((x) => (filter === "all" || x.status === filter) && (!term || [x.name, x.phone, x.suburb, x.service, x.email, x.message].join(" ").toLowerCase().includes(term)));
  }, [quotes, filter, q]);

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-ink/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-28 sm:h-32 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 sm:gap-5">
            <WaterSplash originRef={headerLogoRef} delay={0.3} trigger={splash} />
            <motion.img
              ref={headerLogoRef}
              src="/logo.png"
              alt="Big Rickie's"
              className="h-[64px] sm:h-[104px] w-auto drop-shadow-[0_0_18px_rgba(107,188,253,0.35)]"
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: [0.7, 1.12, 0.96, 1], opacity: [0, 1, 1, 1] }}
              transition={{ duration: 1, delay: 0.1, times: [0, 0.5, 0.75, 1], ease: EASE }}
              data-testid="admin-logo-img"
            />
            <div className="leading-none">
              <div className="font-display font-black uppercase text-white text-xl sm:text-4xl tracking-tight whitespace-nowrap">Rickie's Office</div>
              <div className="font-mono text-[10px] sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] text-brand-light mt-1 whitespace-nowrap">{BUSINESS.short} · Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tab === "quotes" && <button onClick={load} className="btn-ghost !px-3 !py-2 !text-sm" data-testid="admin-refresh-btn"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> <span className="hidden sm:inline">Refresh</span></button>}
            <button onClick={onLogout} className="btn-ghost !px-3 !py-2 !text-sm" data-testid="admin-logout-btn"><LogOut size={14} /> <span className="hidden sm:inline">Lock</span></button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-1 border-b border-white/10 mb-8" data-testid="admin-tabs">
          {[["quotes", "Quote requests", Inbox, quotes.filter((x) => x.status === "new").length], ["gallery", "Website photos", Images, null]].map(([id, label, Icon, badge]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`relative inline-flex items-center gap-2 px-4 py-3 font-display font-bold uppercase tracking-wider text-sm transition-colors ${tab === id ? "text-white" : "text-slate-500 hover:text-slate-300"}`}
            >
              <Icon size={15} /> {label}
              {badge > 0 && <span className="grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-brand text-white font-mono text-[10px]">{badge}</span>}
              {tab === id && <motion.span layoutId="admin-tab-underline" className="absolute left-0 right-0 -bottom-px h-0.5 bg-brand" />}
            </button>
          ))}
        </div>

        {tab === "gallery" && <AdminGallery passcode={passcode} />}

        <div className={tab === "quotes" ? "" : "hidden"} data-testid="admin-quotes-tab">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-8">
          <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`admin-filter-${s}`}
                className={`px-3.5 py-2 rounded-full font-mono text-[11px] uppercase tracking-widest border transition-colors ${filter === s ? "bg-brand border-brand text-white" : "border-white/10 text-slate-400 hover:text-white hover:border-white/30"}`}
              >
                {s} <span className="opacity-60">{counts[s]}</span>
              </button>
            ))}
          </div>
          <label className="relative lg:ml-auto lg:w-72">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, suburb…" className="field !pl-10 !py-2.5" data-testid="admin-search-input" />
          </label>
        </div>

        {loading && quotes.length === 0 ? (
          <div className="py-24 grid place-items-center text-slate-500"><Loader2 className="animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="py-24 text-center text-slate-500" data-testid="admin-empty-state">
            <Inbox className="mx-auto mb-4 text-slate-600" size={40} />
            <div className="font-display font-bold uppercase text-xl text-slate-400">No enquiries here</div>
            <div className="text-sm mt-1">New quote requests from the website will land in this list.</div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="admin-quote-list">
            <AnimatePresence>
              {visible.map((x) => <QuoteRow key={x.id} q={x} onStatus={onStatus} onDelete={onDelete} />)}
            </AnimatePresence>
          </ul>
        )}
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const logout = useCallback(() => setPasscode(""), []);
  return passcode ? <Dashboard passcode={passcode} onLogout={logout} /> : <Login onSuccess={setPasscode} />;
}
